import {
  Component,
  OnInit,
  Inject,
  HostBinding,
  HostListener,
  Renderer2,
  AfterViewInit,
  ChangeDetectorRef,
  DestroyRef,
  ViewEncapsulation
} from '@angular/core';
import { PlatformInformationService } from '../../projects/platform-manager/src/lib/services/platform-information.service';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { ResolutionService } from './core/services/resolution.service';
import { ResponseService } from './core/services/response.service';
import {
  Router,
  NavigationStart,
  ActivatedRoute,
  ParamMap,
  ActivationEnd,
  ChildrenOutletContexts,
  RouterModule
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SsrCookieService as CookieService } from 'ngx-cookie-service-ssr';
import { AppColorService } from './core/services/app-color.service';
import { SwUpdate, VersionDetectedEvent, VersionReadyEvent } from '@angular/service-worker';
import { PlatformassetPipe } from '../../projects/platform-manager/src/public_api';
import { LocalStorageService } from './core/services/local-storage.service';
import { RouteSlideAnimation } from './core/animations/route.animation';
import { filter, startWith } from 'rxjs/operators';
import { NotificationService } from './shared/services/notification.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Error404Component } from './pages/error/error404/error404.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { StructuredDataService } from './shared/services/structured-data.service';
import { CustomScrollRestorationService } from './shared/services/custom-scroll-restoration.service';
import { FooterComponent } from './shared/components/footer/footer.component';
import { MeliboComponent } from './shared/components/melibo/melibo.component';
import { CookieConsentComponent } from './shared/components/cookie-consent/cookie-consent.component';
import { NotificationComponent } from './shared/components/notification/notification.component';

@Component({
  selector: 'app-root',
  // instead of a render blocking main.css, we add it to app component without view encapsulation
  // -> better FCP according to lighthouse
  styleUrls: ['./app.component.scss', '../main.scss'],
  templateUrl: './app.component.html',
  animations: [RouteSlideAnimation],
  encapsulation: ViewEncapsulation.None,
  imports: [
    HeaderComponent,
    RouterModule,

    // defer loaded
    Error404Component,
    FooterComponent,
    MeliboComponent,
    CookieConsentComponent,
    NotificationComponent
  ]
  // TODO: this can only be implemented after EVERY sub-component
  // has been updated to onPush detection or else even stuff like zoom-in will break
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private platformService: PlatformInformationService,
    private resolutionService: ResolutionService,
    private responseService: ResponseService,
    private router: Router,
    private renderer: Renderer2,
    private title: Title,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private cookieService: CookieService,
    private colorService: AppColorService,
    private swUpdate: SwUpdate,
    private assetPipe: PlatformassetPipe,
    private localStorageService: LocalStorageService,
    protected contexts: ChildrenOutletContexts,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private structuredDataService: StructuredDataService,
    private scrollRestorationService: CustomScrollRestorationService,
    private destroyRef: DestroyRef
  ) {}

  @HostBinding('@.disabled')
  public animationsDisabled = this.platformService.getIsBrowser() ? false : true; // disabled when on server

  injectedStyles = false;
  notFound = false;

  showWithLayout = true;
  showShell = !this.activatedRoute.snapshot.queryParamMap.has('app-shell-hidden');

  private tabticktock: string | number | NodeJS.Timeout;
  private oldTitle: string;
  private comeBackTitle: string;
  protected enableMainRouting = false; // init router outlet in template (after init for macrotasking)

  partner = this.platformService.getCurrentPartner();

  getRouteAnimationData() {
    if (this.resolutionService.isSmallerThanLargeDevice()) {
      return this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];
    } else {
      return '';
    }
  }

  ngOnInit() {
    this.animationsDisabled = !this.platformService.getIsBrowser();
    if (this.swUpdate.isEnabled && !this.platformService.isLiveProd()) {
      this.swUpdate.versionUpdates
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(
            (evt): evt is VersionReadyEvent | VersionDetectedEvent =>
              evt.type === 'VERSION_READY' || evt.type === 'VERSION_DETECTED'
          )
        )
        .subscribe({
          next: next => {
            if (next.type === 'VERSION_READY') {
              console.info('current version', next.currentVersion);
              console.info('new version available', next.latestVersion);
              window.location.reload();
            } else {
              console.info('detected new version, start loading', next.version, next.type);
              // New version detected and service worker is about to start to download
              this.notificationService.add({
                loadingIndicator: true,
                transkey: 'APP.update',
                displayTime: 5000,
                id: 'update'
              });
            }
          }
        });
    }
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.structuredDataService.clearMetaTags();
        this.notFound = false;
      }
      if (event instanceof ActivationEnd) {
        if (event.snapshot.component) {
          this.showWithLayout = !event.snapshot.data.showWithoutLayout;
          if (!this.showWithLayout) {
            this.removeTheme();
          }
        }
      }
    });
    this.translateService.onLangChange
      .pipe(startWith({ lang: this.translateService.currentLang }), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const now = new Date();
        this.translateService
          .get('CORE.comeBack', {
            icon: now.getMonth() === 11 && now.getDate() > 21 && now.getDate() < 27 ? '🎄' : '🎿'
          })
          .subscribe((text: string) => {
            this.comeBackTitle = text;
          });
      });
    this.responseService.notFoundEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.notFound = true;
    });
    setTimeout(() => {
      this.setMetaTagColor();
      this.updateColorSchemeForPlatform();
      this.colorService.injectFonts(this.platformService.getCurrentPlatformShortcode());
      this.injectPwaReferences();
      this.setFavicon();
      this.structuredDataService.setCompanyJsonLd();
    });
    if (this.platformService.getIsBrowser()) {
      this.activatedRoute.queryParamMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((paramMap: ParamMap) => {
          if (
            (paramMap.has('gclid') || paramMap.has('gclsrc')) &&
            +paramMap.get('aw_account') !== 4814084532
          ) {
            // set AdWordsCookie
            const dayAfterTomorrow = new Date();
            dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 90); // request from MT: change it to 90 days. Informed him about ITP security in safari
            this.cookieService.set('AdWords', '1', { expires: dayAfterTomorrow });
          }
          this.setMarketingCookies(paramMap);

          if (
            paramMap.has('test') &&
            paramMap.has('version') &&
            !this.platformService.isLiveProd() &&
            this.platformService.getIsBrowser()
          ) {
            this.localStorageService.write(`abTest${paramMap.get('test')}`, paramMap.get('version'));
          }
        });
      this.onResize();
    }
    if (this.platformService.getIsBrowser()) {
      setTimeout(() => {
        this.scrollRestorationService.setupScrollRestorationListener(this.destroyRef);
      });
    }
  }

  setMarketingCookies(paramMap: ParamMap) {
    const cookieMappings: { [key: string]: string } = {
      gclid: 'alpy_gid',
      gbraid: 'alpy_gbid',
      wbraid: 'alpy_wbid',
      fclid: 'alpy_fid',
      mclid: 'alpy_mid'
    };

    const expires = new Date();
    expires.setDate(expires.getDate() + 90);

    for (const param in cookieMappings) {
      if (paramMap.has(param)) {
        this.cookieService.set(cookieMappings[param], paramMap.get(param), { expires, path: '/' });
      }
    }
  }

  ngAfterViewInit(): void {
    // update the width since a scrollbar can affect the real size between service init and user interaction
    // service init -> render page (width: 1200px/lg)
    // -> oh, it is big, add a scrollbar (width: 1994px/md)
    // -> now update ref again
    if (this.platformService.getIsBrowser()) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          this.resolutionService.updateSizeReference(document.body.clientWidth);
        });
      });
    }
    // prevent router from running init cycle in same macrotask as app component
    setTimeout(() => {
      this.enableMainRouting = true;
      this.cdr.detectChanges();
    });
  }

  /**
   * inject PWA needed meta tags for the platform
   * <link rel="manifest" href="/manifest.webmanifest">
   * <meta name="apple-mobile-web-app-capable" content="yes" />
   * <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
   * splashScreens()
   * appIconLinkTags()
   */
  private injectPwaReferences() {
    const meta = this.renderer.createElement('meta');
    meta.name = 'facebook-domain-verification';
    meta.content = 's3rlornachngqdfaj2nzebxxj0iduw';
    this.renderer.appendChild(this.document.head, meta);
    if (!this.document.getElementById('manifest')) {
      // prevent double injects
      const link = this.renderer.createElement('link');
      link.setAttribute('href', this.assetPipe.transform(`manifest.webmanifest`, true, true));
      link.setAttribute('rel', 'manifest');
      link.setAttribute('id', 'manifest');

      const capable = this.renderer.createElement('meta');
      capable.setAttribute('name', 'apple-mobile-web-app-capable');
      capable.setAttribute('content', 'yes');

      const statusBar = this.renderer.createElement('meta');
      statusBar.setAttribute('apple-mobile-web-app-status-bar-style', 'black-translucent');
      [link, capable, statusBar].forEach(link => {
        this.renderer.appendChild(this.document.head, link);
      });
      this.injectSplashscreenLinkTags();
      this.injectAppIconLinkTags();
    }
  }

  /**
   * sets the css values according to the platform.
   * Will also us cssVars to enable IE11
   * to not break down and cry like the piece of garbage it is
   * TODO: dont create a stylesheed, set the colors with javascript
   */
  private async updateColorSchemeForPlatform() {
    this.renderer.addClass(this.document.body, this.platformService.getCurrentPlatformShortcode());
    this.renderer.setAttribute(this.document.body.parentNode, 'lang', this.translateService.currentLang);
    const variables = await this.colorService.getColorFor(this.platformService.getCurrentPlatformShortcode());
    const root = this.document.documentElement;
    this.renderer.setAttribute(
      root,
      'style',
      Object.keys(variables)
        .map(v => {
          return `${v}:${variables[v]}`;
        })
        .join('; ')
    );
  }

  /**
   * Disables all stylesheets
   * @private
   */
  private removeTheme() {
    const themePreload = this.document.getElementById('themepreload');
    if (themePreload) {
      themePreload.remove();
    }
    const theme = this.document.getElementById('theme');
    if (theme) {
      theme.remove();
    }
  }

  /**
   * set the favicon. For platform AR, an SVG-version exists
   * https://realfavicongenerator.net
   * <link rel="apple-touch-icon" sizes="144x144" href="/apple-touch-icon.png">
   * <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
   * <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
   * <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5">
   * <meta name="msapplication-TileColor" content="#da532c">
   */
  private setFavicon() {
    const platformShortCode = this.platformService.getCurrentPlatformShortcode();

    let elems: Array<{
      eltype: string;
      rel: string;
      type?: string;
      href?: string;
      sizes?: string;
      content?: string;
      color?: string;
    }>;
    switch (platformShortCode) {
      case 'es':
        elems = [
          // ToDo: merge injectAppIconTags here, very similar injection routine
          // {eltype: 'link', rel: 'apple-touch-icon', sizes: '144x144', href: 'favicon/apple-touch-icon.png'},
          { eltype: 'link', rel: 'icon', type: 'image/png', sizes: '32x32', href: 'icons/favicon-32x32.png' },
          { eltype: 'link', rel: 'icon', type: 'image/png', sizes: '16x16', href: 'icons/favicon-16x16.png' },
          { eltype: 'link', rel: 'mask-icon', href: 'icons/safari-pinned-tab.svg', color: '#5bbad5' },
          { eltype: 'meta', rel: 'msapplication-TileColor', content: '#da532c' }
        ];
        break;
      case 'ar':
        elems = [
          {
            eltype: 'link',
            rel: 'icon',
            type: 'image/svg+xml',
            href: 'icons/' + platformShortCode.toUpperCase() + '_favicon.svg?v=2'
          },
          {
            eltype: 'link',
            rel: 'icon',
            type: 'image/x-icon',
            href: 'icons/' + platformShortCode.toUpperCase() + '_favicon.ico?v=2'
          },
          {
            eltype: 'link',
            rel: 'apple-touch-icon',
            sizes: '180x180',
            href: 'icons/icon-180x180.png'
          },
          {
            eltype: 'link',
            rel: 'apple-touch-icon',
            sizes: '152x152',
            href: 'icons/icon-152x152.png'
          },
          {
            eltype: 'link',
            rel: 'apple-touch-icon',
            sizes: '120x120',
            href: 'icons/icon-120x120.png'
          }
        ];
        break;
      default:
        elems = [
          {
            eltype: 'link',
            rel: 'icon',
            type: 'image/x-icon',
            href: platformShortCode.toUpperCase() + '_favicon.ico'
          }
        ];
        break;
    }
    elems.forEach((element, index) => {
      if (!this.document.getElementsByName('favicon_' + index).length) {
        const tag = this.renderer.createElement(element.eltype);
        Object.keys(element).forEach(key => {
          if (key !== 'eltype') {
            if (key === 'href') {
              tag.setAttribute(
                key,
                this.assetPipe.transform(
                  element[key],
                  ['es', 'ar'].includes(platformShortCode),
                  ['es', 'ar'].includes(platformShortCode)
                )
              );
            } else {
              tag.setAttribute(key, element[key]);
            }
          }
        });
        this.renderer.appendChild(this.document.head, tag);
      }
    });
  }

  /**
   * adds color to android browser navigation bar
   */
  private setMetaTagColor() {
    if (!this.document.getElementsByName('theme-color').length) {
      const meta = this.renderer.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      let content = '';
      switch (this.platformService.getCurrentPlatformShortcode()) {
        case 'ar':
          content = '#CA4D00';
          break;
        case 'sb':
          content = '#FF2140';
          break;
        case 'sf':
          content = '#0075BE';
          break;
        case 'pf':
          content = '#0075BE';
          break;
        case 'sm':
          content = '#0075BE';
          break;
        case 'du':
          content = '#E4448F';
          break;
        case 'df':
          content = '#E4448F';
          break;
        case 'bp':
          content = '#E9612F';
          break;
        case 'mc':
          content = '#E9612F';
          break;
        case 's2':
          content = '#45aff9';
          break;
        case 'es':
          break;
      }
      meta.setAttribute('content', content);
      this.renderer.appendChild(this.document.head, meta);
    }
  }

  /**
   * adds so called 'splash screens' that will appear on iOS devices
   * when starting the PWA from scratch. Every screen resolution and density
   * in both orientations need to be provided as meta tags with a media query
   * At the moment (2023) there are 32 different images that need to be provided
   * images need to be provided as png in the platform asset folder under splashscreens
   * as WIDTHxHEIGHT.png
   * see Alpy.com
   */
  private injectSplashscreenLinkTags(format = 'png') {
    [
      // density 2
      { deviceWidth: 1024, deviceHeight: 1366, pixelratio: 2 },
      { deviceWidth: 834, deviceHeight: 1194, pixelratio: 2 },
      { deviceWidth: 768, deviceHeight: 1024, pixelratio: 2 },
      { deviceWidth: 834, deviceHeight: 1112, pixelratio: 2 },
      { deviceWidth: 810, deviceHeight: 1080, pixelratio: 2 },
      { deviceWidth: 414, deviceHeight: 896, pixelratio: 2 },
      { deviceWidth: 375, deviceHeight: 667, pixelratio: 2 },
      { deviceWidth: 320, deviceHeight: 568, pixelratio: 2 },

      // density 3
      { deviceWidth: 430, deviceHeight: 932, pixelratio: 3 },
      { deviceWidth: 393, deviceHeight: 852, pixelratio: 3 },
      { deviceWidth: 428, deviceHeight: 926, pixelratio: 3 },
      { deviceWidth: 390, deviceHeight: 844, pixelratio: 3 },
      { deviceWidth: 375, deviceHeight: 812, pixelratio: 3 },
      { deviceWidth: 414, deviceHeight: 896, pixelratio: 3 },
      { deviceWidth: 414, deviceHeight: 736, pixelratio: 3 }
    ].forEach(screen => {
      const metaPortrait = this.renderer.createElement('link');
      metaPortrait.setAttribute('rel', 'apple-touch-startup-image');
      metaPortrait.setAttribute(
        'href',
        this.assetPipe.transform(
          `splashscreens/${screen.deviceWidth * screen.pixelratio}x${screen.deviceHeight * screen.pixelratio}.${format}`,
          true,
          true
        )
      );
      metaPortrait.setAttribute(
        'media',
        `screen and (device-width: ${screen.deviceWidth}px) and (device-height: ${screen.deviceHeight}px) and (-webkit-device-pixel-ratio: ${screen.pixelratio}) and (orientation: portrait)`
      );
      this.renderer.appendChild(this.document.head, metaPortrait);

      const metaLandscape = this.renderer.createElement('link');
      metaLandscape.setAttribute('rel', 'apple-touch-startup-image');
      metaLandscape.setAttribute(
        'href',
        this.assetPipe.transform(
          `splashscreens/${screen.deviceHeight * screen.pixelratio}x${screen.deviceWidth * screen.pixelratio}.${format}`,
          true,
          true
        )
      );
      metaLandscape.setAttribute(
        'media',
        `screen and (device-width: ${screen.deviceWidth}px) and (device-height: ${screen.deviceHeight}px) and (-webkit-device-pixel-ratio: ${screen.pixelratio}) and (orientation: landscape)`
      );
      this.renderer.appendChild(this.document.head, metaLandscape);
    });
  }

  private injectAppIconLinkTags() {
    let list: Array<number>;
    switch (this.platformService.getCurrentPlatformShortcode()) {
      case 'es':
        list = [144];
        break;
      default:
        list = [72, 96, 128, 144, 152, 192, 384, 512];
        break;
    }
    list.forEach(size => {
      const tag = this.renderer.createElement('link');
      tag.setAttribute('rel', 'apple-touch-icon');
      tag.setAttribute('sizes', `${size}x${size}`);
      tag.setAttribute('href', this.assetPipe.transform(`icons/icon-${size}x${size}.png`, true, true));
      this.renderer.appendChild(this.document.head, tag);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.resolutionService.updateSizeReference(document.body.clientWidth);
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    this.oldTitle = this.oldTitle ? this.oldTitle : this.document.title;
    if (this.document.hidden) {
      // start ticktock
      this.oldTitle = this.document.title;
      let tick = true;
      this.tabticktock = setInterval(() => {
        if (tick) {
          this.title.setTitle(this.oldTitle);
        } else {
          this.title.setTitle(this.comeBackTitle);
        }
        tick = !tick;
      }, 5000);
    } else {
      // stop ticktock
      clearInterval(this.tabticktock);
      this.title.setTitle(this.oldTitle);
      this.oldTitle = '';
    }
  }
}
